// Updated api.ts functions to handle long-running requests with Ably
import {
  CourseMessageRequest,
  EditedMessagesRequest,
  SendMessageRequest,
  TTSRequest,
} from '../views/userChat/messages.types';

import ablyClientManager from '../utils/ablyClientManager';
import { platform } from 'os';
import { ProductDraft } from '../context/ProductContext';
import { getIdToken } from './apis/auth';
import { FanDataType } from '../views/userProfile/creatorFanData';

// Type definitions for our mock response
interface MockResponse {
  ok: boolean;
  status: number;
  statusText: string;
  json: () => Promise<any>;
  text: () => Promise<string>;
  arrayBuffer?: () => Promise<ArrayBuffer>;
  blob?: () => Promise<Blob>;
  formData?: () => Promise<FormData>;
  headers: {
    get: (name: string) => string | null;
  };
}

const proxyURL = '/.netlify/functions/api-proxy';
const backgroundProxyURL = '/.netlify/functions/api-proxy-background';
const enableDevTools = process.env.REACT_APP_ENABLE_DEVTOOLS === 'true';
const enableFastTestRoutes =
  process.env.REACT_APP_ENABLE_FAST_TEST_ROUTES === 'true';

// Generate unique job ID
const generateJobId = () => {
  return `job-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

// Generate channel name for job
const generateChannelName = (endpoint, jobId) => {
  // Clean endpoint name for channel
  const cleanEndpoint = endpoint.replace(/[^a-zA-Z0-9]/g, '-');
  return `background-jobs:${cleanEndpoint}:${jobId}`;
};

// Utility function for long-running requests with Ably communication
const makeLongRunningRequest = async (
  endpoint: string,
  method: string = 'POST',
  data?: any,
  authUserId?: string,
  onProgress?: (message: string) => void
): Promise<Response | MockResponse> => {
  if (data?.streaming) {
    const token = await getIdToken();

    const requestOptions: RequestInit = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: JSON.stringify({
        data,
        authUserId,
      }),
    };

    const response = await fetch(
      '/.netlify/functions/chat-stream',
      requestOptions
    );

    return response;
  }

  const longRunningEndpoints = [
    'api/chat',
    'api/courses/instruct',
    'api/courses/index_and_generate_curriculum',
    'api/index_all',
    'google-drive/sync-google-drive-folder',
    'api/journal/chat',
    'api/google_drive/resync',
  ];

  // More precise endpoint matching to avoid false positives
  const isLongRunning = longRunningEndpoints.some((longEndpoint) => {
    // Exact match
    if (endpoint === longEndpoint) return true;

    // For endpoints that should match with query parameters
    if (endpoint.startsWith(longEndpoint + '?')) return true;

    // For endpoints that should match with path parameters
    if (longEndpoint.includes('/') && endpoint.startsWith(longEndpoint + '/'))
      return true;

    // Special case: 'chat' should not match 'chat_messages' or other chat-related endpoints
    if (longEndpoint === 'chat') {
      return endpoint === 'chat' || endpoint.startsWith('chat?');
    }

    if (longEndpoint === 'api/chat') {
      return endpoint === 'api/chat' || endpoint.startsWith('api/chat?');
    }

    return false;
  });

  // For non-long-running requests, use the regular proxy
  if (!isLongRunning) {
    return makeProxyRequest(endpoint, method, data, authUserId);
  }

  // For long-running requests, use background processing with Ably
  try {
    // Generate job ID and channel name
    const jobId = generateJobId();
    const channelName = generateChannelName(endpoint, jobId);

    if (enableDevTools)
      console.log(
        `Starting long-running request for ${endpoint} with jobId: ${jobId}, channel: ${channelName}`
      );

    // Get the channel from our client manager
    const channel = await ablyClientManager.getChannel(channelName);

    let progressCallback: ((message: any) => void) | null = null;
    let completeCallback: ((message: any) => void) | null = null;

    // Create promise that resolves when the background job completes
    const backgroundPromise = new Promise<MockResponse>((resolve, reject) => {
      // Handle progress messages
      progressCallback = (message: any) => {
        if (message.data.jobId === jobId) {
          if (enableDevTools)
            console.log('Progress update:', message.data.message);
          if (onProgress) {
            onProgress(message.data.message);
          }
        }
      };

      // Handle completion messages
      completeCallback = (message: any) => {
        if (message.data.jobId === jobId) {
          if (enableDevTools) console.log('Job completed:', message.data);

          // Clean up subscriptions
          channel.unsubscribe('progress', progressCallback);
          channel.unsubscribe('complete', completeCallback);

          if (message.data.status === 'success') {
            // Create a mock response object that matches the expected format
            const mockResponse: MockResponse = {
              ok: true,
              status: message.data.statusCode || 200,
              statusText: 'OK',
              json: () => Promise.resolve(message.data.data),
              text: () => Promise.resolve(JSON.stringify(message.data.data)),
              headers: {
                get: (name: string) => {
                  if (name.toLowerCase() === 'content-type') {
                    return 'application/json';
                  }
                  return null;
                },
              },
            };
            resolve(mockResponse);
          } else {
            reject(
              new Error(
                message.data.message ||
                  message.data.error ||
                  'Background job failed'
              )
            );
          }
        }
      };

      // Subscribe to channels
      channel.subscribe('progress', progressCallback);
      channel.subscribe('complete', completeCallback);

      // Set a timeout for the entire operation (e.g., 10 minutes)
      setTimeout(
        () => {
          // Clean up subscriptions
          if (progressCallback)
            channel.unsubscribe('progress', progressCallback);
          if (completeCallback)
            channel.unsubscribe('complete', completeCallback);
          reject(new Error('Background job timed out after 10 minutes'));
        },
        10 * 60 * 1000
      ); // 10 minutes
    });

    // Start the background job (fire and forget)
    const requestBody = {
      endpoint,
      method,
      data,
      authUserId,
      jobId,
      ablyChannel: channelName,
    };

    try {
      const token = await getIdToken();

      const requestOptions = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      };

      if (token) {
        requestOptions.headers['Authorization'] = `Bearer ${token}`;
      }

      const backgroundResponse = await fetch(
        backgroundProxyURL,
        requestOptions
      );

      // We only care if the request was accepted, not the response content
      if (!backgroundResponse.ok) {
        // Clean up subscriptions if background job failed to start
        if (progressCallback) channel.unsubscribe('progress', progressCallback);
        if (completeCallback) channel.unsubscribe('complete', completeCallback);

        throw new Error(
          `Failed to start background job: HTTP ${backgroundResponse.status}`
        );
      }

      if (enableDevTools)
        console.log(
          `Background job initiated for ${endpoint} with jobId: ${jobId}`
        );
    } catch (fetchError) {
      // Clean up subscriptions if we couldn't even start the background job
      if (progressCallback) channel.unsubscribe('progress', progressCallback);
      if (completeCallback) channel.unsubscribe('complete', completeCallback);

      console.error('Failed to initiate background job:', fetchError);
      throw new Error(
        `Failed to initiate background job: ${fetchError.message}`
      );
    }

    if (onProgress) {
      onProgress('Background processing started...');
    }

    // Wait for the background job to complete via Ably
    return await backgroundPromise;
  } catch (error) {
    console.error('Long-running request error:', error);

    // Fallback to regular proxy for backward compatibility
    if (enableDevTools)
      console.log('Falling back to regular proxy due to error:', error.message);
    if (onProgress) {
      onProgress('Falling back to standard processing...');
    }

    return makeProxyRequest(endpoint, method, data, authUserId);
  }
};

// Helper function to make proxy requests with timeout handling
const makeProxyRequest = async (
  endpoint: string,
  method: string = 'GET',
  data?: any,
  authUserId?: string
): Promise<Response> => {
  const requestBody = {
    endpoint,
    method,
    data,
    authUserId,
  };

  const requestOptions = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestBody),
  };

  const token = await getIdToken();

  if (token) {
    requestOptions.headers['Authorization'] = `Bearer ${token}`;
  }

  try {
    const response = await fetch(proxyURL, requestOptions);

    // Handle timeout responses specifically
    if (response.status === 408) {
      const errorData = await response.json();
      console.warn(`Request timeout for ${endpoint}:`, errorData.message);

      // You can customize this error handling based on your UI needs
      throw new Error(`Request timed out: ${errorData.message}`);
    }

    return response;
  } catch (error) {
    // If it's a fetch error (network issues, etc.)
    if (error instanceof TypeError && error.message.includes('fetch')) {
      throw new Error(
        'Network error - please check your connection and try again'
      );
    }

    // Re-throw other errors as-is
    throw error;
  }
};

// Export the updated functions with proper typing
export { makeLongRunningRequest, makeProxyRequest };

// Type guard to check if response is our mock response
export const isMockResponse = (
  response: Response | MockResponse
): response is MockResponse => {
  return (
    'headers' in response &&
    typeof response.headers.get === 'function' &&
    !('url' in response)
  );
};

// Helper function to safely handle both Response and MockResponse
export const handleResponse = async (response: Response | MockResponse) => {
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`HTTP ${response.status}: ${errorText}`);
  }
  return response.json();
};

export interface DeleteDataRequest {
  user_name?: string;
  user_id?: string;
  delete_profile?: boolean;
  delete_index?: boolean;
  delete_conversation?: DeleteConversationRequest;
  delete_notifications?: boolean;
  delete_training_info?: boolean;
  delete_documents?: boolean;
  delete_webpages?: boolean;
}

export interface DeleteConversationRequest {
  creator_id: string;
}

// Voice functions - keeping direct calls since they handle binary data/audio
export function cloneVoice(userId: string): any {
  const data = { user_id: userId };
  return makeProxyRequest('api/voices/clone', 'POST', data);
}

export function deleteVoice(userId: string): any {
  const data = { user_id: userId };
  return makeProxyRequest('api/voices/delete', 'POST', data);
}

// Alternative streaming TTS function - more efficient for larger audio files
export function getTextToSpeechStreaming(data: TTSRequest): any {
  const proxyURL = '/.netlify/functions/tts-streaming-proxy';

  const requestOptions = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      data: data,
    }),
  };

  const audioCtx = new AudioContext();

  return fetch(proxyURL, requestOptions)
    .then((response) => {
      if (!response.ok) {
        throw new Error(
          `TTS Streaming Proxy error, status = ${response.status}`
        );
      }
      return response.arrayBuffer();
    })
    .then((buffer) => audioCtx.decodeAudioData(buffer))
    .then((decodedData) => {
      const source = audioCtx.createBufferSource();
      const analyzer = audioCtx.createAnalyser();
      const gainNode = audioCtx.createGain();
      gainNode.gain.value = 1;
      source.buffer = decodedData;
      source.connect(analyzer);
      analyzer.connect(gainNode);
      gainNode.connect(audioCtx.destination);
      return [source, analyzer, gainNode];
    });
}

// Updated main TTS function with emoji removal moved to server-side
export function getTextToSpeech(data: TTSRequest): any {
  const proxyURL = '/.netlify/functions/tts-proxy';
  const audioCtx = new AudioContext();

  return getIdToken()
    .then((token) => {
      const requestOptions = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({ data }),
      };
      return fetch(proxyURL, requestOptions);
    })
    .then(async (response) => {
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          `TTS Proxy error: ${errorData.error || response.statusText}`
        );
      }
      return response.json();
    })
    .then((responseData) => {
      if (!responseData.success || !responseData.audioData) {
        throw new Error('Invalid TTS response format');
      }
      const binaryString = atob(responseData.audioData);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      return bytes.buffer;
    })
    .then((buffer) => audioCtx.decodeAudioData(buffer))
    .then((decodedData) => {
      const source = audioCtx.createBufferSource();
      const analyzer = audioCtx.createAnalyser();
      const gainNode = audioCtx.createGain();
      gainNode.gain.value = 1;
      source.buffer = decodedData;
      source.connect(analyzer);
      analyzer.connect(gainNode);
      gainNode.connect(audioCtx.destination);
      return [source, analyzer, gainNode];
    });
}

export async function getMessageHistory(
  authUserId: string,
  roomId: string,
  isProductChat: boolean = false,
  page: number = 1,
  pageSize: number = 100
) {
  const requestBody = {
    endpoint: isProductChat
      ? `api/user/chat_messages?room_id=${roomId}&page=${page}&pageSize=${pageSize}`
      : `api/user/chat_messages?room_id=${roomId}&page=${page}&pageSize=${pageSize}`,
    method: 'GET',
    authUserId: authUserId,
  };

  return makeProxyRequest(requestBody.endpoint, 'GET', undefined);
}

export function sendMessageToSmartAssistant(
  data: SendMessageRequest,
  isProductChat: boolean = false,
  onProgress?: (message: string) => void
): any {
  return makeLongRunningRequest(
    isProductChat ? 'api/chat' : 'api/chat',
    'POST',
    data,
    undefined,
    onProgress
  );
}

export function buildPersonality(documentId: string): any {
  const data = { doc_id: '' + documentId + '' };
  return makeProxyRequest('indexer', 'POST', data);
}

export function editedMessageResponse(data: EditedMessagesRequest): any {
  return makeProxyRequest('insert_message', 'POST', data);
}

export async function updatePersona(
  userId: string,
  data: { [key: string]: boolean },

  onProgress?: (message: string) => void
): Promise<any> {
  const endpoint = 'api/index_all';
  const jsonObject = {
    user_id: userId,
    ...data,
  };

  return makeLongRunningRequest(
    endpoint,
    'POST',
    jsonObject,
    undefined,
    onProgress
  );
}

export async function deactivateUser(
  reason: string = 'No reason provided'
): Promise<any> {
  const data = { message: reason };

  try {
    const result = await makeProxyRequest('api/user/delete_user', 'POST', data);
    if (!result.ok) throw new Error('Failed to deactivate user');
    const res = await result.json();
    return res;
  } catch (err) {
    console.error('Error deactivating user: ', err);
    throw err;
  }
}

export function deleteData(data: DeleteDataRequest): any {
  return makeProxyRequest('api/delete_data', 'POST', data);
}

export function clearChat(roomId: string): any {
  const endpoint = `api/messages/clear_chat?room_id=${roomId}`;
  return makeProxyRequest(endpoint, 'DELETE', undefined);
}

export async function getProfileByUserId(
  authUserId: string,
  userId: string,
  get_details: boolean = false,
  get_top_streaks: boolean = false
) {
  const endpoint = `api/user/profile?user_id=${userId}&get_details=${get_details}&get_top_streaks=${get_top_streaks}`;

  // For profile requests, we might need the authUserId in headers
  const requestBody = {
    endpoint,
    method: 'GET',
    authUserId: authUserId,
  };

  const requestOptions = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestBody),
  };

  return fetch(proxyURL, requestOptions);
}

export async function getProfileByUsername(
  authUserId: string,
  userName: string,
  get_details: boolean = false,
  get_top_streaks: boolean = false
) {
  const endpoint = `api/user/profile_by_username?user_name=${userName}&get_details=${get_details}&get_top_streaks=${get_top_streaks}`;

  return makeProxyRequest(endpoint, 'GET', undefined);
}

export function deleteFromPersona(
  userId: string,
  llama_index_doc_id: string
): any {
  const data = {
    user_id: userId,
    llama_index_doc_id: [llama_index_doc_id],
  };

  return makeProxyRequest('api/delete_from_index', 'POST', data);
}

export async function fetchLevelQuestionsData(queryParams: any): Promise<any> {
  try {
    const creatorId = queryParams?.queryKey?.[1];
    const levelId = queryParams?.queryKey?.[2];
    const data = {
      user_id: creatorId,
      level: levelId,
    };

    const response = await makeProxyRequest(
      'get_training_questions',
      'POST',
      data
    );
    return response?.json();
  } catch (err) {
    console.log('fetch-level-data-questions-error', err);
    throw err;
  }
}

export function reportPost(
  userId: string,
  postId: string,
  postContent: string = ''
): Promise<any> {
  const data = { userId: userId, contentId: postId };

  return makeProxyRequest('api/email/report', 'POST', data).then(
    async (resp) => {
      if (!resp.ok) {
        const err = await resp.text();
        throw new Error(`HTTP ${resp.status}: ${err}`);
      }
      return resp.json();
    }
  );
}

export function reportComment(
  userId: string,
  postId: string,
  commentId: string,
  commentContent: string = ''
): Promise<any> {
  const data = { userId: userId, contentId: commentId };

  return makeProxyRequest('api/email/report', 'POST', data).then(
    async (resp) => {
      if (!resp.ok) {
        const err = await resp.text();
        throw new Error(`HTTP ${resp.status}: ${err}`);
      }
      return resp.json();
    }
  );
}

export async function updateLevelQuestionsData(
  updatedLevelData: any
): Promise<any> {
  try {
    const response = await makeProxyRequest(
      'api/update_training_questions',
      'POST',
      updatedLevelData
    );
    return response?.json();
  } catch (err) {
    console.log('update-level-data-questions-error', err);
    throw err;
  }
}

export async function fetchCreatorLevelInfo(queryParams: any): Promise<any> {
  try {
    const creatorId = queryParams?.queryKey?.[1];
    const data = { user_id: creatorId };

    const response = await makeProxyRequest(
      'get_train_level_info',
      'POST',
      data
    );
    return response?.json();
  } catch (err) {
    console.log('fetch-level-questions-error', err);
  }
}

export function uploadFacebookAuthResponse(response: any): any {
  try {
    return makeProxyRequest('auth/facebook', 'POST', response);
  } catch (err) {
    console.log('Facebook-authentication-error', err);
  }
}

export function getNotifications(
  userId: string,
  page?: number,
  pageSize?: number
): any {
  const data = {
    user_id: userId,
    page: page,
    pageSize: pageSize,
  };

  return makeProxyRequest('notifications', 'POST', data);
}

export function markNotificationRead(
  userId: string,
  notificationId: string
): any {
  const data = {
    user_id: userId,
    notification_id: notificationId,
  };

  return makeProxyRequest('api/notifications', 'PUT', data);
}

export async function getSortedCreatorList(
  userId: string,
  page?: number,
  pageSize?: number,
  sortBy?: string
): Promise<any> {
  page ||= 1;
  pageSize ||= 10;
  sortBy ||= 'oldestFirst';

  const endpoint = `api/user/creators?${
    userId ? `user_id=${userId}&` : ''
  }page=${page}&pageSize=${pageSize}&sortBy=${sortBy}`;

  return makeProxyRequest(endpoint, 'GET', undefined);
}

export function getIntroMessage(userId: string): any {
  const endpoint = `api/generate_intro?user_id=${userId}`;
  return makeProxyRequest(endpoint, 'GET', undefined);
}

export function getCreatorsList(
  userId: string,
  searchQuery: string,
  page?: number,
  pageSize?: number
): any {
  page ||= 1;
  pageSize ||= 10;
  const endpoint = `api/search/${userId}?page=${page}&pageSize=${pageSize}&query=${searchQuery}`;

  return makeProxyRequest(endpoint, 'GET', undefined);
}

export function getCorporateAccounts(
  enterpriseName: string,
  page = 1,
  pageSize = 10,
  sortBy = 'aToz'
) {
  const endpoint = `api/corporate_accounts/${enterpriseName}?page=${page}&pageSize=${pageSize}&sortBy=${sortBy}`;
  return makeProxyRequest(endpoint, 'GET', undefined);
}

///////////////////////////////////////////////
// Email Related Functions
///////////////////////////////////////////////
export function getUnsubscribeSecretToken(
  userId: string,
  userToken: string = ''
) {
  const endpoint = `api/email/get_user_secret?user_id=${userId}`;

  return makeProxyRequest(endpoint, 'GET', undefined, userToken);
}
export function getUserEmailPreferences(secretId: string) {
  const endpoint = `api/email/preferences?secret_id=${secretId}`;

  return makeProxyRequest(endpoint, 'GET', undefined);
}

export function updateUserEmailPreferences(
  secretId: string,
  emailPreferences: Record<string, boolean>
) {
  const data = { secretId, emailPreferences };

  return makeProxyRequest('api/email/preferences', 'PUT', data)
    .then((response) => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.json();
    })
    .catch((error) => {
      console.error('Error updating preferences:', error);
    });
}

export function sendEmailWithTemplate(email: string) {
  const data = { email: email };

  return makeProxyRequest('api/email/send-email-with-template', 'POST', data)
    .then((response) => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.json();
    })
    .then((data) => {
      console.log('Email sent successfully:', data);
      return data;
    })
    .catch((error) => {
      console.error('Error sending email:', error);
    });
}

export function sendWelcomeEmail(user_id: string, userToken: string = '') {
  const data = { userId: user_id };

  return makeProxyRequest(
    'api/email/send-welcome-email',
    'POST',
    data,
    userToken
  )
    .then((response) => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.json();
    })
    .then((data) => {
      console.log('Email sent successfully:', data);
      return data;
    })
    .catch((error) => {
      console.error('Error sending email:', error);
    });
}

export function sendCreatorBroadcast(
  creatorId: string,
  subject: string,
  message: string,
  creatorName?: string,
  maxRecipients?: number,
  dryRun?: boolean
) {
  const endpoint = `api/email/broadcasts/creators/${creatorId}`;
  const data = {
    template: 'creator_broadcast',
    dynamic_data: {
      recipientName: 'Test Recipient', // Backend will replace with actual names
      creatorName: creatorName || '', // Will be populated from profile data
      campaignId: `manual-campaign-${Date.now()}`,
      subject: subject,
      message: message,
      ctaUrl: 'https://soopra.ai',
      unsubscribeLink: 'https://soopra.ai/unsubscribe',
    },
    max_recipients: maxRecipients || 5000,
    include_unsubscribe_link: true,
    dry_run: dryRun || false,
  };
  return makeProxyRequest(endpoint, 'POST', data);
}

////////////////////////////////////////////////////////////////////
// Course related functions
////////////////////////////////////////////////////////////////////

export function searchCoursesList(
  searchQuery: string,
  page?: number,
  pageSize?: number
) {
  page ||= 1;
  pageSize ||= 10;
  const endpoint = `api/courses/search?page=${page}&pageSize=${pageSize}&query=${searchQuery}`;

  return makeProxyRequest(endpoint, 'GET', undefined);
}

export function getSortedCoursesList(
  page?: number,
  pageSize?: number,
  filterBy?: string
) {
  page ||= 1;
  pageSize ||= 10;
  filterBy ||= 'None';
  const endpoint = `api/courses/search?page=${page}&pageSize=${pageSize}&filterBy=${filterBy}`;
  return makeProxyRequest(endpoint, 'GET', undefined);
}

export function getCourseInfo(courseId: string) {
  const endpoint = `api/courses/courses/?course_id=${courseId}`;
  return makeProxyRequest(endpoint, 'GET', undefined);
}

export const createCourseDoc = async (
  userId: string,
  courseTitle: string,
  subject: string,
  grade: string,
  coursePrompt: string,
  numModules: number,
  teachingTone: string
) => {
  const data = {
    course_title: courseTitle,
    user_id: userId,
    user_description: coursePrompt,
    subject: subject,
    grade: grade,
    module_length: numModules,
    teaching_tone: teachingTone,
  };

  try {
    const response = await makeProxyRequest(
      'api/courses/courses',
      'POST',
      data
    );
    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }
    return response.json();
  } catch (error) {
    console.error('Error in createCurriculum API call:', error);
    throw error;
  }
};

export function generateCurriculum(
  courseId: string,
  userId: string,
  courseDocuments: any,
  onProgress?: (message: string) => void,
  checkRevoked: boolean = true
) {
  const data = {
    course_id: courseId,
    course_documents: courseDocuments,
    user_id: userId,
  };

  return makeLongRunningRequest(
    `api/courses/index_and_generate_curriculum?check_revoked=${checkRevoked}`,
    'PUT',
    data,
    undefined,
    onProgress
  ).then((response) => {
    if (!response.ok) {
      return response.json().then((errorInfo) => {
        console.error('Error response from server:', errorInfo);
        throw new Error(`HTTP error! Status: ${response.status}`);
      });
    }
    return response.json();
  });
}

export function updateCurriculum(
  courseId: string,
  updatedCourseInfo: any,
  creationDone = false
) {
  const data = {
    course_id: courseId,
    creation_done: creationDone,
    ...updatedCourseInfo,
  };

  return makeProxyRequest('api/courses/courses', 'PUT', data).then(
    (response) => {
      if (!response.ok) {
        return response.json().then((errorInfo) => {
          console.error('Error response from server:', errorInfo);
          throw new Error(`HTTP error! Status: ${response.status}`);
        });
      }
      return response.json();
    }
  );
}

export async function fetchCourseIntroMessage(
  courseId: string,
  generateNew: boolean
) {
  const endpoint = `api/courses/gen_intro?course_id=${courseId}&generate_new=${generateNew}`;
  return makeProxyRequest(endpoint, 'GET', undefined);
}

export async function setCourseIntroMessage(courseId: string, message: string) {
  const data = { course_id: courseId, message: message };

  try {
    const response = await makeProxyRequest(
      'api/courses/gen_intro',
      'POST',
      data
    );
    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }
    return response.json();
  } catch (error) {
    console.error('Error in setCourseIntroMessage API call:', error);
    throw error;
  }
}

export function sendMessageInCourse(
  data: CourseMessageRequest,
  onProgress?: (message: string) => void,
  checkRevoked: boolean = true
): any {
  return makeLongRunningRequest(
    `api/courses/instruct?check_revoked=${checkRevoked}`,
    'POST',
    data,
    undefined,
    onProgress
  );
}

export const fetchCoursesByCreator = async (creatorId: string) => {
  const endpoint = `api/courses/list/${creatorId}`;

  return makeProxyRequest(endpoint, 'GET', undefined)
    .then(
      (response) => {
        if (response.ok) {
          return response.json();
        }
      },
      () => {}
    )
    .then((courses) => {
      return (courses || []).filter((course) => course?.creation_done === true);
    })
    .catch((error) => {
      console.error('Error in fetchCoursesByCreator API call:', error);
    });
};

export const fetchCourseResultsByStudent = async (
  studentId: string,
  checkRevoked: boolean = true
) => {
  const endpoint = `api/courses/results?student_id=${studentId}&check_revoked=${checkRevoked}`;

  try {
    const response = await makeProxyRequest(endpoint, 'GET', undefined);
    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    const results = await response.json();
    return results;
  } catch (error) {
    console.error('Error in fetchCourseResultsByStudent API call:', error);
    throw error;
  }
};

export const fetchResultsByStudentAndCourse = async (
  studentId: string,
  courseId: string,
  checkRevoked: boolean = true
) => {
  const endpoint = `api/courses/results?student_id=${studentId}&course_id=${courseId}&check_revoked=${checkRevoked}`;

  try {
    const response = await makeProxyRequest(endpoint, 'GET', undefined);
    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    const results = await response.json();
    return results;
  } catch (error) {
    console.error('Error in fetchResultsByStudentAndCourse API call:', error);
    throw error;
  }
};

export const fetchResultsByCourse = async (
  courseId: string,
  checkRevoked: boolean = true
) => {
  const endpoint = `api/courses/results?course_id=${courseId}&check_revoked=${checkRevoked}`;

  try {
    const response = await makeProxyRequest(endpoint, 'GET', undefined);
    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    const results = await response.json();
    return results;
  } catch (error) {
    console.error('Error in fetchResultsByCourse API call:', error);
    throw error;
  }
};

export const getCurriculumDetails = async (courseId: string) => {
  const endpoint = `api/courses/courses/${courseId}`;

  try {
    const response = await makeProxyRequest(endpoint, 'GET', undefined);
    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }
    return response.json();
  } catch (error) {
    console.error('Error in getCurriculumDetails API call:', error);
    throw error;
  }
};

export function deleteCourse(courseId: string) {
  const data = { course_id: courseId };

  return makeProxyRequest('api/courses/courses', 'DELETE', data)
    .then((response) => {
      if (!response.ok) {
        return response.json().then((errorInfo) => {
          console.error('Error response from server:', errorInfo);
          throw new Error(`HTTP error! Status: ${response.status}`);
        });
      }
      return response.json();
    })
    .catch((error) => {
      console.error('Error in deleteCourse API call:', error);
      throw error;
    });
}

export async function fetchCourseProgress(
  roomId: string,
  checkRevoked: boolean = true
): Promise<any> {
  const encodedRoomId = encodeURIComponent(roomId);
  const endpoint = `api/courses/progress?room_id=${encodedRoomId}&check_revoked=${checkRevoked}`;

  try {
    const response = await makeProxyRequest(endpoint, 'GET', undefined);
    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Error fetching course progress:', error);
    throw error;
  }
}

export async function updateCourseResults(
  courseId: string,
  studentId: string,
  moduleNum: number,
  results: any,
  checkRevoked: boolean = true
): Promise<any> {
  const resultsData = {
    score: results.filter((result) => result.isCorrect).length,
    questions: results.map((result) => ({
      question: result.question,
      selectedOption: result.selectedOption,
      correctOption: result.correctOption,
      isCorrect: result.isCorrect,
    })),
    module_num: moduleNum,
  };

  const data = {
    course_id: courseId,
    student_id: studentId,
    results: resultsData,
  };

  try {
    const response = await makeProxyRequest(
      `api/courses/results?check_revoked=${checkRevoked}`,
      'PUT',
      data
    );
    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Error updating course results:', error);
    throw error;
  }
}

export const uploadCourseBanner = async (
  course_id: string,
  unsplash_url: string
) => {
  const proxyURL = '/.netlify/functions/formdata-proxy';

  const formData = new FormData();

  // Add proxy configuration
  formData.append('endpoint', 'upload_image');
  // Note: uploadCourseBanner doesn't seem to need authUserId, but we can add it if needed
  // formData.append('authUserId', '');

  // Add actual form data
  formData.append('course_id', course_id);
  formData.append('unsplash_url', unsplash_url);

  const token = await getIdToken();

  const requestOptions = {
    method: 'POST',
    headers: {},
    body: formData,
  };

  if (token) {
    requestOptions.headers['Authorization'] = `Bearer ${token}`;
  }

  try {
    const response = await fetch(proxyURL, requestOptions);
    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }
    return;
  } catch (error) {
    console.error('Error in uploadCourseBanner API call:', error);
    throw error;
  }
};

export const searchUnsplash = async (
  query: string,
  page: number = 1,
  per_page: number = 10,
  orientation: 'landscape' | 'portrait' | 'squarish' = 'landscape'
) => {
  const proxyURL = '/.netlify/functions/unsplash-proxy';

  // Build query parameters
  const params = new URLSearchParams({
    query: query,
    page: page.toString(),
    per_page: per_page.toString(),
    orientation: orientation,
  });

  try {
    const response = await fetch(`${proxyURL}?${params.toString()}`);

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        `Unsplash API error: ${errorData.error || response.statusText}`
      );
    }

    return response.json();
  } catch (error) {
    console.error('Error in searchUnsplash API call:', error);
    throw error;
  }
};

export function sendCourseCompletionEmail(
  user_id: string,
  course_id: string,
  courses_link: string
): Promise<any> {
  const data = {
    userId: user_id,
    courseId: course_id,
    courseLink: courses_link,
  };

  return makeProxyRequest(
    'api/email/send-course-completion-email',
    'POST',
    data
  ).then((response) => {
    if (!response.ok) {
      return response.json().then((err) => {
        throw new Error(
          `HTTP error! status: ${response.status}, message: ${err.message}`
        );
      });
    }
    return response.json();
  });
}

////////////////////////////////////////////////////////////////////
// Monetization related functions
////////////////////////////////////////////////////////////////////

export async function createCheckoutSession(
  user_id: string,
  email: string,
  item: string,
  customerId?: string,
  itemId?: string,
  redirectURL?: string,
  isPremium?: boolean,
  trialPeriod?: number,
  referral?: string
) {
  let origin = window.location.origin + '/';

  const data = {
    email: email,
    item: item,
    origin_domain: origin,
  };

  if (customerId) data['customer'] = customerId;
  if (itemId) data['item_id'] = itemId;
  if (redirectURL) data['redirect_url'] = redirectURL;
  if (isPremium) data['is_premium'] = true;
  if (trialPeriod) data['trial_period'] = trialPeriod;
  if (referral) data['referral'] = referral;

  return makeProxyRequest(
    `api/payments/checkout-session/${user_id}`,
    'POST',
    data
  );
}

export async function getPaymentsHistory(
  user_id: string,
  page?: number,
  page_size?: number
) {
  page ||= 1;
  page_size ||= 10;
  const endpoint = `api/payments/history/${user_id}?page=${page}&pageSize=${page_size}`;

  return makeProxyRequest(endpoint, 'GET', undefined);
}

export async function getCheckoutSessionStatus(
  user_id: string,
  session_id: string
) {
  const endpoint = `api/payments/checkout-session/${user_id}?session_id=${session_id}`;
  return makeProxyRequest(endpoint, 'GET', undefined);
}

export async function getPrices(items: string[]) {
  let itemsString = '[' + items.join(', ') + ']';
  const endpoint = `api/payments/prices?items=${itemsString}`;

  return makeProxyRequest(endpoint, 'GET', undefined);
}

export async function getRevenue(
  user_id: string,
  product_id: string,
  month: number,
  year: number
) {
  const endpoint = `api/payments/revenue/${user_id}?product_id=${product_id}&month=${month}&year=${year}`;
  return makeProxyRequest(endpoint, 'GET', undefined);
}

export async function getSubscriptions(user_id: string) {
  const endpoint = `api/payments/subscriptions/${user_id}`;
  return makeProxyRequest(endpoint, 'GET', undefined);
}

export async function setCustomPrice(
  user_id: string,
  mode: 'create' | 'update',
  name?: string,
  description?: string,
  price?: number,
  interval?: string,
  prod_id?: string
) {
  const data = { user_id: user_id };

  if (name) data['name'] = name;
  if (description) data['description'] = description;
  if (price || price === 0) data['price'] = price;
  if (interval) data['interval'] = interval;
  if (prod_id) data['product_id'] = prod_id;

  const method = mode === 'create' ? 'POST' : 'PUT';
  return makeProxyRequest('api/payments/prices', method, data);
}

////////////////////////////////////////////////////////////////////

export async function indexPrefetch(user_id: string) {
  const endpoint = `api/index_prefetch?user_id=${user_id}`;
  return makeProxyRequest(endpoint, 'GET', undefined);
}

export async function getTwitterAuthUrl(): Promise<string> {
  const response = await makeProxyRequest('twitter/auth-url', 'GET', undefined);

  if (!response.ok) {
    throw new Error('Failed to fetch Twitter auth URL');
  }

  const data = await response.json();
  return data.url;
}

export async function handleTwitterCallback(
  code: string,
  userId: string
): Promise<any> {
  const endpoint = `api/auth/twitter/callback?code=${code}&user_id=${userId}`;

  try {
    const response = await makeProxyRequest(endpoint, 'GET', undefined);

    if (!response.ok) {
      throw new Error('Failed to fetch Twitter user data');
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error handling Twitter callback:', error);
    throw error;
  }
}

export async function fetchTwitterData(userId: string): Promise<any> {
  const endpoint = `api/auth/twitter/data/${userId}`;

  return makeProxyRequest(endpoint, 'GET', undefined)
    .then(
      (response) => {
        if (response.ok) {
          return response.json();
        }
      },
      () => {
        return { is_twitter_authenticated: false };
      }
    )
    .catch((err) => {
      console.error('Error in fetchTwitterData, ', err);
      return { is_twitter_authenticated: false };
    });
}

export async function handleSaveJournalChat(
  userId: string,
  journalTitle: string,
  journalText: string,

  onProgress?: (message: string) => void
): Promise<any> {
  try {
    const data = {
      user_id: userId,
      message: journalText,
      title: journalTitle,
      room_id: `${userId}.bot.journal`,
    };

    const response = await makeLongRunningRequest(
      'api/journal/chat',
      'POST',
      data,
      undefined,
      onProgress
    );
    return response.ok;
  } catch (error) {
    console.log('Journal Save error: ', error);
  }
}

export const fetchSearchResponseData = async (query: string) => {
  const endpoint = `/moe/search?query=${query}`;

  try {
    const response = await makeProxyRequest(endpoint, 'GET', undefined);
    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }
    const results = await response.json();
    return results;
  } catch (error) {
    console.error('Error in fetchSearchResponseData API call:', error);
    throw error;
  }
};

export const fetchRandomCreator = async () => {
  const endpoint = `/moe/im_feeling_lucky`;

  try {
    const response = await makeProxyRequest(endpoint, 'GET', undefined);
    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }
    const results = await response.json();
    return results;
  } catch (error) {
    console.error('Error in fetchSearchResponseData API call:', error);
    throw error;
  }
};

export const getSearchSummaryData = async (responses: any) => {
  const data = { responses: responses };

  try {
    const response = await makeProxyRequest('/moe/summarize', 'POST', data);
    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    const results = await response.json();
    return results;
  } catch (error) {
    console.error('Error in getSearchSummaryData API call:', error);
    throw error;
  }
};

export async function fetchTprCalcSheetData(): Promise<string[][] | null> {
  const proxyURL = '/.netlify/functions/google-sheets-proxy';

  try {
    // Use default sheetId for Frank's TPR calculator
    const params = new URLSearchParams({ sheetId: 'frank_tpr_calc' });
    const response = await fetch(`${proxyURL}?${params.toString()}`);

    if (!response.ok) {
      const errorData = await response.json();
      console.warn(
        `Failed to fetch sheet data: ${errorData.error || response.statusText}`
      );
      return null;
    }

    const data = await response.json();

    // Return the values array for backward compatibility with existing component usage
    return data.values || null;
  } catch (error) {
    console.error('Error fetching Google Sheet data:', error);
    return null;
  }
}

export const handleGoogleDriveResync = async (
  userId: string,
  link: string[],

  onProgress?: (message: string) => void,
  checkRevoked: boolean = true
) => {
  // const extractFolderId = (url: string): string | null => {
  //   if (!url) return null;

  //   const match = url.match(/\/folders\/([a-zA-Z0-9_-]+)/);
  //   return match ? match[1] : url;
  // };

  // const folderIds = link
  //   .map(url => extractFolderId(url))
  //   .filter(Boolean);

  const data = {
    user_id: userId,
    google_drive_folder_id: link,
  };

  if (
    !link.length ||
    !(process.env.REACT_APP_ENABLE_GOOGLE_DRIVE_INTEGRATION === 'true')
  ) {
    return Promise.resolve();
  }

  return makeLongRunningRequest(
    `api/google_drive/resync?check_revoked=${checkRevoked}`,
    'POST',
    data,
    undefined,
    onProgress
  )
    .then((response) => {
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      return response.json();
    })
    .catch((error) => {
      console.error('Error in Google Drive Resync API call:', error);
      throw error;
    });
};

export const getGoogleDriveResyncUpdates = async (
  userId: string,
  link: string
) => {
  const endpoint = `api/google_drive/index_progress?user_id=${userId}&&google_drive_folder_link=${link}`;

  try {
    const response = await makeProxyRequest(endpoint, 'GET', undefined);

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    const results = await response.json();
    return results;
  } catch (error) {
    console.error('Error in Google Drive Resync Update API call:', error);
    throw error;
  }
};

export const fetchFeaturedCreators = async () => {
  const endpoint = `api/featured/creators`;
  return makeProxyRequest(endpoint, 'GET', undefined);
};

export const fetchFeaturedCourses = async () => {
  const endpoint = `api/featured/courses`;
  return makeProxyRequest(endpoint, 'GET', undefined);
};

export const fetchFanList = async (
  creator_id: string,
  num_fans?: number
): Promise<FanDataType> => {
  num_fans ||= 10;
  const endpoint = `api/get_fan_list?creator_id=${creator_id}&display_limit=${num_fans}`;

  try {
    const resp = await makeProxyRequest(endpoint, 'GET', undefined);
    if (resp.ok) {
      const data: FanDataType = await resp.json();
      return data;
    }
  } catch (err) {
    console.log('Error in fetching fan list', err);
  }

  return { fans: [], total_fans: 0 };
};

export function getExistingJournalEntries(
  userId: string,
  page?: number,
  pageSize?: number
): any {
  page ||= 1;
  pageSize ||= 10;
  const endpoint = `api/journal/get_history?user_id=${userId}&page=${page}&per_page=${pageSize}`;

  return makeProxyRequest(endpoint, 'GET', undefined);
}

export async function handleUpdateJournalChat(
  userId: string,
  journalTitle: string,
  journalText: string,
  journal_id: string
): Promise<any> {
  try {
    const data = {
      user_id: userId,
      data: {
        message: journalText,
        title: journalTitle,
      },
      journal_id: journal_id,
    };

    const response = await makeProxyRequest(
      'api/journal/update_entry',
      'PUT',
      data
    );
    return response.ok;
  } catch (error) {
    console.log('Journal Save error: ', error);
  }
}

export async function handleDeleteJournalChat(
  userId: string,
  journalId: string
): Promise<any> {
  try {
    const data = {
      user_id: userId,
      journal_id: [journalId],
    };

    const response = await makeProxyRequest(
      'api/journal/delete_entry',
      'DELETE',
      data
    );
    return response.ok;
  } catch (error) {
    console.log('Journal Delete error: ', error);
  }
}

export function configureMeeting(meetingId: string, userIds: string[]): any {
  const data = {
    meeting_id: meetingId,
    user_ids: userIds,
  };

  return makeProxyRequest('meetings/configure', 'POST', data);
}

/////////////////////////////////////////////////////////////
// Product API
////////////////////////////////////////////////////////////
export async function extractProductInfo(
  user_id: string = '',
  product_link: string = '',
  userToken: string = ''
): Promise<any> {
  const endpoint = `api/product/extract_url_info?url=${product_link}&user_id=${user_id}`;
  try {
    const response = await makeProxyRequest(endpoint, 'GET', undefined);
    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }
    const results = await response.json();
    return results;
  } catch (error) {
    console.error('Error in Processing Product Link', error);
    throw error;
  }
}

export async function getAllProducts(
  userId: string = '',
  include_inactive: boolean = true,
  userToken: string = ''
): Promise<any> {
  const endpoint = `api/product?user_id=${userId}&include_inactive=${include_inactive}`;
  try {
    const response = await makeProxyRequest(
      endpoint,
      'GET',
      undefined,
      undefined
    );
    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }
    const results = await response.json();
    return results;
  } catch (error) {
    console.error('Error in Getting Products', error);
    throw error;
  }
}

export async function getProductFromId(
  userId: string = '',
  productId: string = '',
  include_inactive: boolean = true,
  userToken: string = ''
): Promise<any> {
  const endpoint = `api/product?user_id=${userId}&product_id=${productId}&include_inactive=${include_inactive}`;
  try {
    const response = await makeProxyRequest(
      endpoint,
      'GET',
      undefined,
      undefined
    );
    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }
    const results = await response.json();
    return results;
  } catch (error) {
    console.error(`Error in Getting Product from id: ${productId}`, error);
    throw error;
  }
}

export async function publishProduct(
  userId: string = '',
  productData: ProductDraft = null,
  userToken: string = ''
): Promise<any> {
  const proxyURL = '/.netlify/functions/formdata-proxy';
  const formData = new FormData();
  formData.append('method', 'POST');
  formData.append('endpoint', `api/product`);
  formData.append('user_id', userId);
  formData.append('product_url', productData.productURL);
  formData.append('name', productData.name);
  formData.append('description', productData.description);
  if (productData.image) formData.append('image_file', productData.image);
  else formData.append('image_url', productData.imageURL);

  const token = await getIdToken();

  const requestOptions = {
    method: 'POST',
    headers: {},
    body: formData,
  };

  if (token) {
    requestOptions.headers['Authorization'] = `Bearer ${token}`;
  }

  try {
    const response = await fetch(proxyURL, requestOptions);
    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }
    const results = await response.json();
    return results;
  } catch (error) {
    console.error('Error in Publishing Product', error);
    throw error;
  }
}

export async function updateProduct(
  userId: string = '',
  productData: any = null,
  userToken: string = ''
): Promise<any> {
  const proxyURL = '/.netlify/functions/formdata-proxy';

  const formData = new FormData();
  formData.append('method', 'PUT');
  formData.append('endpoint', `api/product`);
  formData.append('user_id', userId);
  formData.append('product_id', productData.product_id);
  if (productData.name) formData.append('name', productData.name);
  if (productData.description)
    formData.append('description', productData.description);
  if (productData.isActive !== undefined) {
    formData.append('is_active', String(productData.isActive));
  }
  if (productData.image instanceof File) {
    formData.append('image_file', productData.image);
  }

  const token = await getIdToken();

  const requestOptions = {
    method: 'POST',
    headers: {},
    body: formData,
  };

  if (token) {
    requestOptions.headers['Authorization'] = `Bearer ${token}`;
  }

  try {
    const response = await fetch(proxyURL, requestOptions);
    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }
    const results = await response.json();
    return results;
  } catch (error) {
    console.error('Error in Updating Product', error);
    throw error;
  }
}

export async function deleteProduct(
  userId: string = '',
  productId: string = '',
  userToken: string = ''
): Promise<any> {
  const endpoint = `api/product`;
  const data = {
    user_id: userId,
    product_id: productId,
  };
  try {
    const response = await makeProxyRequest(
      endpoint,
      'DELETE',
      data,
      undefined
    );
    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }
    const results = await response.json();
    return results;
  } catch (error) {
    console.error('Error in Deleting Product', error);
    throw error;
  }
}

export async function toggleProductStatus(
  userId: string = '',
  productId: string = '',
  userToken: string = ''
): Promise<any> {
  const endpoint = `api/product/toggle_active_status?user_id=${userId}&product_id=${productId}`;
  try {
    const response = await makeProxyRequest(
      endpoint,
      'PATCH',
      undefined,
      undefined
    );
    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }
    const results = await response.json();
    return results;
  } catch (error) {
    console.error('Error in Toggling Product Status', error);
    throw error;
  }
}

export async function incrementProductField(
  userId: string = '',
  productId: string = '',
  field: 'view_count' | 'click_count' = 'view_count',
  userToken: string = ''
): Promise<any> {
  const endpoint = `api/product/increment?user_id=${userId}&product_id=${productId}&field=${field}`;

  try {
    const response = await makeProxyRequest(
      endpoint,
      'PATCH',
      undefined,
      undefined
    );
    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }
    const results = await response.json();
    return results;
  } catch (error) {
    console.error(`Error in Incrementing ${field} field`, error);
    throw error;
  }
}

export async function getProductFromShortCode(
  userId: string = '',
  shortCode: string = ''
): Promise<any> {
  const endpoint = `api/product/short_link?user_id=${userId}&short_code=${shortCode}`;
  try {
    const response = await makeProxyRequest(endpoint, 'GET', undefined);
    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }
    const result = await response.json();
    return result;
  } catch (error) {
    console.error(
      `Error in Getting Product from short code: ${shortCode}`,
      error
    );
    throw error;
  }
}

//////////////////////////////////////////////////////////
// Contest
/////////////////////////////////////////////////////////

export async function getLeaderboard(
  page: number,
  limit?: number
): Promise<any> {
  try {
    const endpoint = `api/contest/leaderboard?page=${page}&limit=${limit ?? 5}`;
    const response = await makeProxyRequest(endpoint, 'GET', undefined);
    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }
    const result = await response.json();
    return result;
  } catch (error) {
    console.error(`Error in Getting leaderboard`, error);
    throw error;
  }
}

export function sendNewCreatorFromContestEmail(
  user_id: string,
  userToken: string = ''
) {
  const data = { userId: user_id };

  return makeProxyRequest(
    '/email/contest/send-new-creator-from-contest',
    'POST',
    data,
    userToken
  )
    .then((response) => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.json();
    })
    .catch((error) => {
      console.error('Error sending Creator Contest Welcome Email:', error);
    });
}
export function sendWelcomeFromContestEmail(
  user_id: string,
  userToken: string = ''
) {
  const data = { userId: user_id };

  return makeProxyRequest(
    'api/email/contest/send-new-user-from-contest',
    'POST',
    data,
    userToken
  )
    .then((response) => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.json();
    })
    .catch((error) => {
      console.error('Error sending Contest User Welcome Email:', error);
    });
}

// Sitemaps indexing
export function extractWebsiteSitemaps(url: string, userToken: string = '') {
  const endpoint = `api/get_sitemap_info?url=${url}`;

  return makeLongRunningRequest(endpoint, 'GET', undefined)
    .then((response) => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.json();
    })
    .catch((error) => {
      console.error('Error sending extracting website sitemap:', error);
    });
}

export function subscribeToMailingList(email: string, source?: string) {
  const data: { email: string; source?: string } = { email };
  if (source) {
    data.source = source;
  }

  return makeProxyRequest('/api/mailing_list', 'POST', data)
    .then((response) => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.json();
    })
    .catch((error) => {
      console.error('Error subscribing to mailing list:', error);
      throw error;
    });
}

export async function submitOnboardingFeedback(
  message: string[],
  checkRevoked: boolean = true
): Promise<any> {
  const endpoint = `api/feedback/onboarding?check_revoked=${checkRevoked}`;
  const data = { message };

  try {
    const response = await makeProxyRequest(endpoint, 'POST', data);

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }
    return response.json();
  } catch (error) {
    console.error('Error submitting onboarding feedback:', error);
    throw error;
  }
}

export async function GoogleDriveValidate(driveLink: string) {
  const endpoint = `api/google_drive/validate?google_drive_folder_link=${encodeURIComponent(
    driveLink
  )}`;

  const response = await makeLongRunningRequest(endpoint, 'GET', undefined);

  if (!response.ok) {
    throw new Error(`HTTP error! Status: ${response.status}`);
  }

  return response.json();
}

export async function googleDriveResyncProgress(userId: string, link: string) {
  const endpoint = `api/google_drive/resync-progress?user_id=${userId}&google_drive_folder_link=${encodeURIComponent(
    link
  )}`;

  const response = await makeLongRunningRequest(endpoint, 'GET', undefined);

  if (!response.ok) {
    throw new Error(`HTTP error! Status: ${response.status}`);
  }

  return response.json();
}

export async function whatsappConsent(
  userId: string,
  mobileNumber: string,
  checkRevoked: boolean = true
) {
  try {
    const endpoint = `/api/whatsapp/consent?check_revoked=${checkRevoked}`;

    const data = {
      user_id: userId,
      mobile_number: mobileNumber,
    };

    const response = await makeProxyRequest(endpoint, 'POST', data);

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    return response.json();
  } catch (error) {
    throw error;
  }
}

export async function getWhatsappConsentStatus(userId: string) {
  try {
    const endpoint = `api/whatsapp/consent?user_id=${userId}`;

    const response = await makeProxyRequest(endpoint, 'GET', undefined);

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    return response.json();
  } catch (error) {
    throw error;
  }
}

export async function whatsappConnect(
  roomId: string,
  checkRevoked: boolean = true
) {
  try {
    const endpoint = `api/whatsapp/connect?check_revoked=${checkRevoked}`;

    const data = {
      room_id: roomId,
    };

    const response = await makeProxyRequest(endpoint, 'POST', data);

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    return response.json();
  } catch (error) {
    throw error;
  }
}

export async function getOnBoradingDetailedProgress() {
  const endpoint = `api/gamification/score/detailed`;

  return makeProxyRequest(endpoint, 'GET', undefined);
}

export async function suggestUsernames(fullName: string, count: number = 5) {
  const endpoint = `api/user/suggest_usernames`;

  const data = {
    full_name: fullName,
    count,
  };

  return makeProxyRequest(endpoint, 'POST', data);
}
